/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import MsDyn365, { ContentEditableEvent, IImageData, IImageSettings, Image, RichTextComponent } from '@msdyn365-commerce/core';
import * as React from 'react';

import { IMfrmRibbonData } from './mfrm-ribbon.data';
import { IMfrmRibbonProps } from './mfrm-ribbon.props.autogenerated';

import { Button } from '@msdyn365-commerce-modules/utilities';

export interface IMfrmRibbonViewProps extends IMfrmRibbonProps<IMfrmRibbonData> {
    image?: IImageData;
    headerText?: React.ReactNode;
    text?: React.ReactNode;
    ctaText?: React.ReactNode;
    ctaButton?: React.ReactNode;
    backgroundClassName?: string;
    imageBorderClassName?: string;
    ribbonHeightClassName?: string;
    textContainerClassName?: string;
    imageContainerClassName?: string;
}

/**
 *
 * MfrmRibbon component
 * @extends {React.PureComponent<IMfrmRibbonProps<IMfrmRibbonData>>}
 */
class MfrmRibbon extends React.PureComponent<IMfrmRibbonProps<IMfrmRibbonData>> {
    public render(): JSX.Element | null {
        const { headerText, text, ctaText, imageBorderOption, tealiumEvent } = this.props.config;
        const ctaLinkUrl = this.props.config.ctaLink?.linkUrl.destinationUrl;
        const backgroundColor = this.props.config.className;
        const avatarBleedLayout = this.props.config.avatarBleedLayout;

        const backgroundClassName =
            backgroundColor === 'ribbonbkgcolor__lightBlue' ? 'mfrm-ribbon__bkg-light-blue'
          : backgroundColor === 'ribbonbkgcolor__red' ? 'mfrm-ribbon__bkg-red'
          : 'mfrm-ribbon__bkg-blue';

        const buttonClassName =
          backgroundColor === 'ribbonbkgcolor__lightBlue' ? 'mfrm-ribbon__blue-cta'
        : backgroundColor === 'ribbonbkgcolor__red' ? 'mfrm-ribbon__blue-cta'
        : 'mfrm-ribbon__red-cta';

        const imageBorderClassName = (imageBorderOption) ? 'mfrm-ribbon__img-border' : 'mfrm-ribbon__img-no-border';

        let ribbonHeightClassName = (avatarBleedLayout) ? "mfrm-ribbon__banner-bleed" : "mfrm-ribbon__banner-no-bleed";

        if(!headerText && !ctaText){
            if(avatarBleedLayout){
                ribbonHeightClassName = 'mfrm-ribbon__banner-subtext-only-bleed';
            }else{
                ribbonHeightClassName = 'mfrm-ribbon__banner-subtext-only';
            }
        }

        const textContainerClassName = (!headerText && !ctaText) ? 'mfrm-ribbon__text-container-subtext-only' : 'mfrm-ribbon__text-container';
        const imageContainerClassName = (headerText && ctaText) ? 'mfrm-mobile-image-container' : 'mfrm-mobile-image-container-subtext-only';

        const handleClick = () => {
            if (MsDyn365.isBrowser) {
                const pageURL = MsDyn365.isBrowser && window.location.pathname;
                // @ts-ignore
                if ('utag' in window) {
                    // @ts-ignore
                    utag.link({
                        event: tealiumEvent?.event || 'mattress-matcher-content-card',
                        event_category: tealiumEvent?.event_category || 'mattress matcher content card',
                        event_action: tealiumEvent?.event_action || 'start button CTA click',
                        event_label: tealiumEvent?.event_label ? `${tealiumEvent?.event_label} ${pageURL}` : `sleep disruptor ${pageURL}`,
                        event_noninteraction: tealiumEvent?.event_noninteraction === true ? 'true' : 'false'
                    });
                }
                window.location.href = ctaLinkUrl!;
            }
        };

        const renderHeaderText = headerText && (
            <RichTextComponent
                text={headerText}
                className='mfrm-ribbon__header-text'
                editProps={{ onEdit: this.handleTextChange, requestContext: this.props.context.request }}
            />
        );

        const renderText = text && (
            <RichTextComponent
                text={text}
                className='mfrm-ribbon__sub-text'
                editProps={{ onEdit: this.handleTextChange, requestContext: this.props.context.request }}
            />
        );

        const renderCtaText = ctaText && (
            <RichTextComponent
                text={ctaText}
                className='mfrm-ribbon__button-text'
                editProps={{ onEdit: this.handleTextChange, requestContext: this.props.context.request }}
            />
        );

        const renderButton = ctaText && (
            <div className='mfrm-ribbon__container'>
                <Button
                    href={ctaLinkUrl}
                    className={buttonClassName}
                    onClick={handleClick}
                >
                    {renderCtaText}
                </Button>
            </div>
        );

        const ribbonViewProps = {
            ...this.props,
            image: this._getRibbonImage(),
            headerText: renderHeaderText,
            text: renderText,
            ctaText: renderCtaText,
            ctaButton: renderButton,
            backgroundClassName: backgroundClassName,
            imageBorderClassName: imageBorderClassName,
            ribbonHeightClassName: ribbonHeightClassName,
            textContainerClassName: textContainerClassName,
            imageContainerClassName: imageContainerClassName
        };

        return (this.props.renderView(ribbonViewProps));
    }

    public handleTextChange = (event: ContentEditableEvent) => this.props.config.text = event.target.value;

    private _getRibbonImage(): React.ReactNode {
        const { image } = this.props.config;

        const defaultImageSettings: IImageSettings = {
            viewports: {
                xs: { q: 'w=110&h=110&m=8', w: 0, h: 0 },
                sm: { q: 'w=110&h=110&m=8', w: 0, h: 0 },
                md: { q: 'w=110&h=110&m=8', w: 0, h: 0 },
                lg: { q: 'w=110&h=110&m=8', w: 0, h: 0 }
            },
            lazyload: true
        };

        return (
            <Image {...image} alt={image?.altText} src={image?.src || ''} gridSettings={this.props.context.request.gridSettings!} imageSettings={image && image.imageSettings || defaultImageSettings} />
        );
    }
}

export default MfrmRibbon;
